import { useState, useCallback, useRef } from "react";
import { checkTermsStatus, acceptTerms } from "../api/client";
import type { TermsType, TermsStatus } from "../api/types";
import { useAuthContext } from "../providers/AuthProvider";

const STORAGE_KEY_WALLET = "unlock-terms"; // 基於 wallet_address 查詢（買家流程）
const STORAGE_KEY_USER = "unlock-terms-user"; // 基於 JWT user_id 查詢（賣家流程）
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

// 未登入（買家）的快取結構
interface WalletCachedTermsStatus {
  walletAddress: string;
  status: TermsStatus;
  timestamp: number;
}

// 已登入（賣家）的快取結構
interface UserCachedTermsStatus {
  status: TermsStatus;
  timestamp: number;
}

interface TermsState {
  showModal: boolean;
  modalTermsType: TermsType | null;
  isLoading: boolean;
}

type PromiseResolver = (value: boolean) => void;

export function useTerms() {
  const { isAuthenticated } = useAuthContext();
  const [state, setState] = useState<TermsState>({
    showModal: false,
    modalTermsType: null,
    isLoading: false,
  });

  // Store promise resolver for the current modal
  const resolverRef = useRef<PromiseResolver | null>(null);

  // Get cached status from localStorage (for wallet-based cache, used by buyers)
  const getWalletCachedStatus = useCallback((walletAddress: string): TermsStatus | null => {
    try {
      const cached = localStorage.getItem(STORAGE_KEY_WALLET);
      if (!cached) return null;

      const data: WalletCachedTermsStatus = JSON.parse(cached);

      // Check if cache is for the same wallet and not expired
      if (
        data.walletAddress.toLowerCase() !== walletAddress.toLowerCase() ||
        Date.now() - data.timestamp > CACHE_TTL
      ) {
        localStorage.removeItem(STORAGE_KEY_WALLET);
        return null;
      }

      return data.status;
    } catch {
      localStorage.removeItem(STORAGE_KEY_WALLET);
      return null;
    }
  }, []);

  // Get cached status for authenticated user (used by sellers)
  const getUserCachedStatus = useCallback((): TermsStatus | null => {
    try {
      const cached = localStorage.getItem(STORAGE_KEY_USER);
      if (!cached) return null;

      const data: UserCachedTermsStatus = JSON.parse(cached);

      // Check if cache is not expired
      if (Date.now() - data.timestamp > CACHE_TTL) {
        localStorage.removeItem(STORAGE_KEY_USER);
        return null;
      }

      return data.status;
    } catch {
      localStorage.removeItem(STORAGE_KEY_USER);
      return null;
    }
  }, []);

  // Save status to localStorage cache (wallet-based, for buyers)
  const setWalletCachedStatus = useCallback((walletAddress: string, status: TermsStatus) => {
    const data: WalletCachedTermsStatus = {
      walletAddress,
      status,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY_WALLET, JSON.stringify(data));
  }, []);

  // Save status to localStorage cache (user-based, for sellers)
  const setUserCachedStatus = useCallback((status: TermsStatus) => {
    const data: UserCachedTermsStatus = {
      status,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(data));
  }, []);

  // Helper: 將 termsType 對應的欄位設為 true
  const applyTermAccepted = (status: TermsStatus, termsType: TermsType): TermsStatus => {
    const updated = { ...status };
    switch (termsType) {
      case "TOS":
        updated.tos = true;
        break;
      case "BUYER_DISCLAIMER":
        updated.buyer_disclaimer = true;
        break;
      case "SELLER_DISCLAIMER":
        updated.seller_disclaimer = true;
        break;
    }
    return updated;
  };

  // Update single term in cache (handles both wallet-based and user-based)
  // 已登入用戶：優先更新 user-based cache（即使有 walletAddress）
  // 未登入用戶：更新 wallet-based cache
  const updateCachedTerm = useCallback((walletAddress: string | undefined, termsType: TermsType) => {
    // 已登入用戶：更新 user-based cache（賣家流程用 user_id 查詢）
    if (isAuthenticated) {
      const cached = getUserCachedStatus();
      if (cached) {
        setUserCachedStatus(applyTermAccepted(cached, termsType));
      }
    }
    // 有 walletAddress：也更新 wallet-based cache（買家流程或雙重身份）
    if (walletAddress) {
      const cached = getWalletCachedStatus(walletAddress);
      if (cached) {
        setWalletCachedStatus(walletAddress, applyTermAccepted(cached, termsType));
      }
    }
  }, [isAuthenticated, getUserCachedStatus, getWalletCachedStatus, setUserCachedStatus, setWalletCachedStatus]);

  // Check terms status from API
  const fetchTermsStatus = useCallback(async (walletAddress?: string): Promise<TermsStatus | null> => {
    try {
      // 買家流程：總是帶 wallet_address，因為 getPermit 用 wallet_address 檢查
      // 賣家流程（無 walletAddress）：用 user_id
      const response = await checkTermsStatus(walletAddress);
      if (response.code === 0) {
        if (walletAddress) {
          setWalletCachedStatus(walletAddress, response.data);
        } else if (isAuthenticated) {
          setUserCachedStatus(response.data);
        }
        return response.data;
      }
      return null;
    } catch {
      return null;
    }
  }, [isAuthenticated, setUserCachedStatus, setWalletCachedStatus]);

  // Check if terms accepted and show modal if not
  // walletAddress 對已登入用戶是可選的
  const checkAndPrompt = useCallback(async (
    walletAddress: string | undefined,
    termsType: TermsType
  ): Promise<boolean> => {
    // 1. 檢查 localStorage 有沒有快取
    // 買家流程（有 walletAddress）：用 wallet-based cache
    // 賣家流程（無 walletAddress）：用 user-based cache
    let status: TermsStatus | null = null;
    if (walletAddress) {
      status = getWalletCachedStatus(walletAddress);
    } else if (isAuthenticated) {
      status = getUserCachedStatus();
    }

    // 2. 沒有快取 → 呼叫 API → 存到 localStorage
    if (!status) {
      status = await fetchTermsStatus(walletAddress);
    }

    // API 也失敗了，允許通過（後端會在實際操作時驗證）
    if (!status) {
      return true;
    }

    // 3. 檢查對應 term 的值
    const isAccepted = (() => {
      switch (termsType) {
        case "TOS":
          return status.tos;
        case "BUYER_DISCLAIMER":
          return status.buyer_disclaimer;
        case "SELLER_DISCLAIMER":
          return status.seller_disclaimer;
      }
    })();

    // 已同意 → 通過
    if (isAccepted) {
      return true;
    }

    // 4. 沒同意 → 彈窗 → 同意後發 API
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setState({
        showModal: true,
        modalTermsType: termsType,
        isLoading: false,
      });
    });
  }, [isAuthenticated, getUserCachedStatus, getWalletCachedStatus, fetchTermsStatus]);

  // Accept terms
  // walletAddress 仍為必須，因為後端 acceptTerms API 需要它
  const accept = useCallback(async (
    walletAddress: string,
    termsType: TermsType
  ): Promise<boolean> => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await acceptTerms(walletAddress, termsType);
      if (response.code === 0) {
        // Update cache（傳入 walletAddress，讓 updateCachedTerm 依據登入狀態決定更新哪個快取）
        updateCachedTerm(walletAddress, termsType);

        // Close modal and resolve promise
        setState({
          showModal: false,
          modalTermsType: null,
          isLoading: false,
        });

        if (resolverRef.current) {
          resolverRef.current(true);
          resolverRef.current = null;
        }

        return true;
      } else {
        setState((prev) => ({ ...prev, isLoading: false }));
        return false;
      }
    } catch {
      setState((prev) => ({ ...prev, isLoading: false }));
      return false;
    }
  }, [updateCachedTerm]);

  // Close modal (only for non-TOS modals)
  const closeModal = useCallback(() => {
    setState({
      showModal: false,
      modalTermsType: null,
      isLoading: false,
    });

    if (resolverRef.current) {
      resolverRef.current(false);
      resolverRef.current = null;
    }
  }, []);

  // Clear cache (useful for testing or when switching wallets/users)
  const clearCache = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY_WALLET);
    localStorage.removeItem(STORAGE_KEY_USER);
  }, []);

  return {
    showModal: state.showModal,
    modalTermsType: state.modalTermsType,
    isLoading: state.isLoading,
    checkAndPrompt,
    accept,
    closeModal,
    clearCache,
  };
}
